import { X } from 'lucide-react'
import { colors } from '@/styles/colors'
import { useTranslation } from '@/i18n'

interface AuditLog {
  id: string
  event_type: string
  event_category: string
  resource_type: string
  resource_id: string | null
  resource_name: string | null
  actor_id: string
  actor_email: string | null
  actor_name: string | null
  uploaded_by_id: string | null
  uploaded_by_name: string | null
  ip_address: string | null
  user_agent: string | null
  action_description: string
  created_at: string
  metadata: Record<string, unknown>
  agent_context: Record<string, unknown> | null
}

interface Props {
  log: AuditLog
  onClose: () => void
}

function useEventTypeLabels() {
  const { t } = useTranslation()
  return {
    document_upload: t('common:audit_log.event_type_labels.document_upload'),
    document_delete: t('common:audit_log.event_type_labels.document_delete'),
    document_update: t('common:audit_log.event_type_labels.document_update'),
    document_view: t('common:audit_log.event_type_labels.document_view'),
    document_download: t('common:audit_log.event_type_labels.document_download'),
    document_metadata_update: t('common:audit_log.event_type_labels.metadata_update'),
  }
}

export default function AuditLogDetailsModal({ log, onClose }: Props) {
  const { t } = useTranslation()
  const EVENT_TYPE_LABELS = useEventTypeLabels()
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-border px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">{t('common:audit_log.details_modal.title')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Event Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('common:audit_log.details_modal.event_info')}</h3>
            <div className="bg-gray-50 dark:bg-dark-muted rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.event_type')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                  {EVENT_TYPE_LABELS[log.event_type as keyof typeof EVENT_TYPE_LABELS] || log.event_type}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.category')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text capitalize">
                  {log.event_category}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.date_time')}</span>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
                    {new Date(log.created_at).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                    {new Date(log.created_at).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.description')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text text-right max-w-sm">
                  {log.action_description}
                </span>
              </div>
            </div>
          </div>

          {/* Resource Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('common:audit_log.details_modal.resource')}</h3>
            <div className="bg-gray-50 dark:bg-dark-muted rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.type')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text capitalize">
                  {log.resource_type}
                </span>
              </div>
              {log.resource_id && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.id')}</span>
                  <code className="text-xs font-mono text-gray-900 dark:text-dark-text bg-white dark:bg-dark-card px-2 py-1 rounded border border-gray-200 dark:border-dark-border">
                    {log.resource_id}
                  </code>
                </div>
              )}
              {log.resource_name && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.name')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text text-right max-w-sm break-words">
                    {log.resource_name}
                  </span>
                </div>
              )}
              {log.uploaded_by_id && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.uploaded_by')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text text-right max-w-sm break-words">
                    {log.uploaded_by_name || t('common:audit_log.unknown')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actor Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('common:audit_log.details_modal.actor')}</h3>
            <div className="bg-gray-50 dark:bg-dark-muted rounded-lg p-4 space-y-3">
              {log.actor_name && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.name')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text">{log.actor_name}</span>
                </div>
              )}
              {log.actor_email && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.email')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text">{log.actor_email}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.user_id')}</span>
                <code className="text-xs font-mono text-gray-900 dark:text-dark-text bg-white dark:bg-dark-card px-2 py-1 rounded border border-gray-200 dark:border-dark-border">
                  {log.actor_id}
                </code>
              </div>
              {log.ip_address && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.ip_address')}</span>
                  <code className="text-xs font-mono text-gray-900 dark:text-dark-text bg-white dark:bg-dark-card px-2 py-1 rounded border border-gray-200 dark:border-dark-border">
                    {log.ip_address}
                  </code>
                </div>
              )}
              {log.user_agent && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('common:audit_log.details_modal.user_agent')}</span>
                  <span className="text-xs text-gray-900 dark:text-dark-text text-right max-w-sm break-all font-mono bg-white dark:bg-dark-card px-2 py-1 rounded border border-gray-200 dark:border-dark-border">
                    {log.user_agent}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          {Object.keys(log.metadata || {}).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('common:audit_log.details_modal.metadata')}</h3>
              <div className="bg-gray-50 dark:bg-dark-muted rounded-lg p-4">
                <pre className="text-xs text-gray-900 dark:text-dark-text overflow-auto max-h-64 font-mono">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Agent Context (if any) */}
          {log.agent_context && Object.keys(log.agent_context).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('common:audit_log.details_modal.agent_context')}</h3>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <pre className="text-xs text-blue-900 dark:text-blue-300 overflow-auto max-h-64 font-mono">
                  {JSON.stringify(log.agent_context, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-dark-card border-t border-gray-200 dark:border-dark-border px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg font-medium text-white transition-colors"
            style={{ backgroundColor: colors.purple[600] }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.purple[700]
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.purple[600]
            }}
          >
            {t('common:audit_log.details_modal.close')}
          </button>
        </div>
      </div>
    </div>
  )
}
